/**
 * @deprecated This file is deprecated and in migration to the new analytics architecture.
 * @see {@link ../../core/analytics/README.md} for the new analytics system.
 * @todo Migrate to the new analytics system in src/core/analytics/
 */
import { trackEventService } from "@func-core/core/application/services/analytics-service";
import { createExperimentStorageAdapter } from "@func-core/core/data/adapters/experiment-storage-adapter";
import { getWindow } from "@utils/browser-features";

export enum Events {
  ACCESS_VIEWED_BROWSER = "Access Viewed",
  APPLE_PAY_VIEWED_BROWSER = "Apple Pay Viewed",
  ADDRESS_VIEWED_BROWSER = "Address Viewed",
  BINDING_VIEWED_BROWSER = "Binding Viewed",
  BLOCK_COLLAPSED = "Block Collapsed",
  BLOCK_EXPANDED = "Block Expanded",
  CHECKOUT_VIEWED_BROWSER = "Checkout Viewed",
  CHECKOUT_SUCCESS_VIEWED_BROWSER = "Checkout Success Viewed",
  CHIP_VIEWED_BROWSER = "Chip Viewed",
  CLICKED_CTA = "Clicked CTA",
  CLICKED_EXTERNAL_LINK = "Clicked external link",
  CLICKED_GO_TO_PAYMENT = "Clicked go to payment cta",
  DATE_SELECTED = "Date selected",
  DETAILS_VIEWED_BROWSER = "Details Viewed",
  ELEMENT_SHOWN = "Element shown",
  ERROR_OCCURRED = "Error Occurred",
  FORM_ANSWERED = "Form answered",
  HAS_CHIP_BROWSER = "Has chip Viewed",
  HEALTH_REPORT_DISPLAYED_BROWSER = "Health Report Displayed",
  HEALTH_REPORT_CTA_CLICKED = "Health Report CTA Clicked",
  ITEM_SELECTED = "Item Selected",
  MISSING_ANSWER = "Missing Answer",
  DIALOG_CLOSED = "Dialog Closed",
  DIALOG_OPENED = "Dialog Opened",
  NO_CHIP_VIEWED_BROWSER = "No chip Viewed",
  ONBOARDING_VIEWED_BROWSER = "Onboarding Viewed",
  PET_FAMILY_VIEWED_BROWSER = "Pet family Viewed",
  PET_HAS_FAMILY = "Pet has family",
  PET_INFO_EDITED = "Pet info edited",
  PET_PARENT_VIEWED_BROWSER = "Pet parent Viewed",
  PHONE_SUBMITTED = "Phone Submitted",
  PRODUCT_BOUGHT = "Product Bought",
  QUOTES_DISPLAYED_BROWSER = "Quotes Displayed",
  REDIRECT_TO_TRUSTPILOT = "Redirect to Trustpilot",
  SECTION_VIEWED_BROWSER = "Section Viewed",
  SMALL_PRINT_PRE_EXISTING_CONDITIONS_VIEWED_BROWSER = "Small print - Pre-existing conditions Viewed",
  SMALL_PRINT_START_VIEWED_BROWSER = "Small print - Start Viewed",
  SMALL_PRINT_WAITING_PERIOD_VIEWED_BROWSER = "Small print - Waiting Period Viewed",
  START_TYPING_ON_FIELD = "Start typing on field",
  SWITCH_TO_MONTHLY_ERROR_VIEWED_BROWSER = "Switch to monthly error Viewed",
  SWITCH_TO_MONTHLY_LOADER_VIEWED_BROWSER = "Switch to monthly loader Viewed",
  SWITCH_TO_MONTHLY_SUCCESS_VIEWED_BROWSER = "Switch to monthly success Viewed",
  SWITCH_TO_MONTHLY_VIEWED_BROWSER = "Switch to monthly Viewed",
  TAPPED_ON_CANCEL_ACTION = "Tapped on cancel action",
  TAPPED_ON_EDIT = "Tapped on edit",
  TAPPED_ON_EDIT_LINK = "Tapped on edit link",
  TAPPED_ON_SAVE_ACTION = "Tapped on save action",
  TURNED_OFF = "Turned Off",
  TURNED_ON = "Turned On",
  UNCOVERED_PET = "Uncovered Pet",
  WELCOME_VIEWED_BROWSER = "Welcome Viewed",
}

const shouldUseSegment = (eventName: string): boolean => {
  if (eventName.endsWith("Viewed")) {
    return eventName !== Events.SECTION_VIEWED_BROWSER;
  }

  return eventName === Events.PHONE_SUBMITTED;
};

export function track(event: string, properties = {}): void {
  if (shouldUseSegment(event)) {
    const w = getWindow();
    const experimentsList = getExperimentsToTrack();

    if (!w?.analytics?.track) {
      return;
    }

    w.analytics.track(event, { page: w.location.href, ...properties, ...{ experimentsList } });

    return;
  }

  trackEventService(event, properties);
}

export async function trackWithPromise(event: string, properties = {}): Promise<void> {
  if (shouldUseSegment(event)) {
    return new Promise((resolve, reject) => {
      const w = getWindow();
      const experimentsList = getExperimentsToTrack();

      if (!w?.analytics?.track) {
        resolve();

        return;
      }

      try {
        w.analytics.track(
          event,
          { page: w.location.href, ...properties, ...{ experimentsList } },
          resolve
        );
      } catch (error) {
        reject(error);
      }
    });
  }

  trackEventService(event, properties);

  return Promise.resolve();
}

export function trackChoice(e: { target?: HTMLInputElement }, properties = {}): void {
  const { target } = e;
  const fullProperties = {
    target: target?.type === "checkbox" ? target.checked : target?.value,
    ...properties,
  };
  const eventName = target?.checked ? Events.TURNED_ON : Events.TURNED_OFF;

  track(eventName, fullProperties);
}

export function trackSectionVisibility(
  sectionName: string,
  secondsVisible: number,
  properties = {}
): void {
  const fullProperties = { section: sectionName, seconds: secondsVisible, ...properties };

  track(Events.SECTION_VIEWED_BROWSER, fullProperties);
}

export function trackLink(element: HTMLAnchorElement, eventName: string, properties = {}): void {
  const w = getWindow();
  const experimentsList = getExperimentsToTrack();

  if (!w?.analytics?.trackLink) {
    return;
  }

  w.analytics.trackLink(element, eventName, {
    page: w.location.href,
    ...properties,
    ...{ experimentsList },
  });
}

const formValueHasChanged = (oldValue: string | undefined, newValue: string | undefined): boolean =>
  oldValue !== newValue;

export const trackFormChanges = <T>(
  eventSender: string,
  newValues: T,
  oldValues: Record<string, string>,
  fieldsToTrack: Array<keyof T>
): void => {
  const trackedEventData: Record<string, any> = {
    eventSender,
    newValues,
    oldValues,
  };

  fieldsToTrack.forEach((field) => {
    trackedEventData[`${String(field)}Changed`] = formValueHasChanged(
      oldValues[String(field)],
      newValues[field] as unknown as string
    );
  });

  track(Events.FORM_ANSWERED, trackedEventData);
};

export const getExperimentsToTrack = (): Record<string, string> => {
  try {
    const adapter = createExperimentStorageAdapter();
    const assignments = adapter.getAllAssignments();

    const experimentAssignments = Object.values(assignments).reduce((acc, assignment) => {
      acc[assignment.experimentId] = assignment.variantId;

      return acc;
    }, {});

    return experimentAssignments;
  } catch (error) {
    return {};
  }
};
